import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { clienteEObra, data, faixaProfundidade, rotulo } from '../formato';
import { Icone } from '../Icone';
import { LeitorQr } from '../LeitorQr';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import { Seletor, SeletorDeCliente, SeletorDeOs, type OpcaoDeFiltro } from '../Filtros';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, ResumoQueFiltra, RodapeDaLista,
  usePaginacao, type FiltroAtivo, type ItemDoResumo,
} from '../BarraDeLista';
import { Periodo } from '../Periodo';
import {
  BarraDeAvanco, Carregando, ColunaOrdenavel, ErroDaApi, JanelaDocumento,
  Selo, Vazio, useEstreito, useOrdenacao,
} from '../ui';
import type { Tom } from '../ui';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * O acervo de amostras.
 *
 * Até aqui só se chegava a uma amostra por dois caminhos: lendo o QR da
 * etiqueta, ou entrando pelo recebimento que a criou. Os dois pressupõem
 * que você **já sabe qual amostra quer**.
 *
 * A pergunta que faltava é a inversa, e é a que o laboratório faz o dia
 * inteiro: *quais amostras estão esperando ensaio?*, *o que veio daquela
 * obra?*, *o que está para vencer o prazo de guarda?*. Sem esta tela a
 * resposta era abrir o banco — e é exatamente o tipo de buraco que faz
 * um técnico voltar para a planilha.
 *
 * O leitor de QR fica aqui também, e não só no recebimento: na
 * prateleira, o caminho mais curto entre o tubo na mão e a tela dele é a
 * câmera, não a digitação de `L2026-0001-03`.
 */

/** Um local do cadastro de armazenamento (0062). */
interface LocalOpcao {
  id: string; nome: string; tipo_nome: string;
  armazenadas: number; situacao: string;
}

interface Amostra {
  id: string;
  codigo: string;
  status: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  tipo_amostra: string | null;
  tipo_acondicionamento: string | null;
  cliente: string | null;
  obra_nome: string | null;
  os_numero: string | null;
  localizacao_fisica: string | null;
  datahora_recebimento: string | null;
  dias_para_descarte: number | null;
  camada: string | null;
  // ── custódia (0062) ──
  estado_custodia: string;
  local_nome: string | null;
  onde_esta: string;
  precisa_guardar: boolean;
  // ── o que falta nela (0067) ──
  ensaios: number | string;
  ensaios_aprovados: number | string;
  ensaios_pendentes: number | string;
  avanco_pct: number | null;
  ensaios_pendentes_texto: string | null;
  dias_no_laboratorio: number | string | null;
  sem_os: boolean;
}

/**
 * O tom do selo segue o **ciclo de vida**, não o alfabeto:
 * o que ainda vai ser trabalhado é neutro, o que está em uso é
 * andamento, o que acabou é ok, e o que deu errado é crítico.
 */
const TOM: Record<string, Tom> = {
  recebida: 'andamento',
  aceita: 'ok',
  aceita_com_ressalva: 'atencao',
  rejeitada: 'critico',
  armazenada: 'neutro',
  em_ensaio: 'andamento',
  consumida: 'neutro',
  subdividida: 'neutro',
  devolvida: 'neutro',
  descartada: 'neutro',
};

/**
 * O selo da CUSTÓDIA, que é outro eixo.
 *
 * Até a onda 23 havia um selo só, e ACEITA, EM ENSAIO e DESCARTADA
 * disputavam o mesmo lugar — sendo que os três respondem perguntas
 * diferentes: *como ela foi aceita na porta* e *onde ela está no ciclo*.
 * Uma amostra pode estar aceita com ressalva E ensaiando ao mesmo tempo;
 * com um selo só, uma das duas verdades se perdia.
 */
const TOM_CUSTODIA: Record<string, Tom> = {
  a_guardar: 'atencao',
  armazenada: 'neutro',
  em_preparacao: 'andamento',
  ensaiando: 'andamento',
  encerrada: 'neutro',
};

const ESTAGIO: Record<string, string> = {
  a_guardar: 'Falta guardar',
  armazenada: 'Armazenada',
  em_preparacao: 'Em preparação',
  ensaiando: 'Ensaiando',
  encerrada: 'Encerrada',
};

/**
 * Os filtros que o laboratório usa de verdade, na ordem em que pergunta.
 *
 * «Falta guardar» vem primeiro depois de «Todas» porque é o único que
 * pede ação de alguém. Os outros respondem onde as coisas estão; esse
 * responde o que está solto pelo laboratório.
 */
/* Os estágios de custódia, na lista suspensa (Testesolo, 02/09/2026). */
const SITUACOES: OpcaoDeFiltro[] = [
  { valor: 'a_guardar', texto: 'Falta guardar' },
  { valor: 'armazenada', texto: 'Na prateleira' },
  { valor: 'em_preparacao', texto: 'Em preparação' },
  { valor: 'ensaiando', texto: 'Ensaiando' },
  { valor: 'encerrada', texto: 'Encerradas' },
];

/**
 * Como ordenar — e por que não é só «mais recente primeiro».
 *
 * O padrão continua sendo a chegada mais recente, que é o que se espera
 * de um acervo. `idade` é a outra pergunta, e é a que decide o que sai
 * da prateleira: amostra parada ocupa espaço físico, e espaço físico
 * acaba.
 */
const ORDENS = [
  { chave: 'codigo', texto: 'mais recentes' },
  { chave: 'idade', texto: 'aqui há mais tempo' },
  { chave: 'pendencia', texto: 'mais ensaios pendentes' },
] as const;

export function Amostras() {
  const navegar = useNavigate();
  /* O celular (309): cartões, «Selecionar» e o «Ler QR» de ícone. */
  const estreito = useEstreito(640);
  const [linhas, setLinhas] = useState<Amostra[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  /* O filtro pode vir do endereço — o Painel manda para cá com
     `?estado=a_guardar` (UX-061); a busca também (`?busca=`), como na
     lista de OS. */
  const [params] = useSearchParams();
  const [busca, setBusca] = useState(() => params.get('busca') ?? '');
  const [estado, setEstado] = useState<string | null>(() => params.get('estado'));
  const [cliente, setCliente] = useState<string | null>(() => params.get('cliente'));
  /* «em amostras é importante também filtrar por OS» (Humberto, 14/09/2026).
     A rota já aceitava `os`; faltava o seletor. */
  const [os, setOs] = useState<string | null>(() => params.get('os'));
  const [lendo, setLendo] = useState(false);
  /*
   * «Com ensaio pendente», «Sem OS» e «Falta guardar» eram a faixa «e
   * também»; viraram o resumo que filtra (onda 271), recortado aqui na
   * lista que já chegou — os números não encolhem quando um está escolhido.
   */
  const [recorte, setRecorte] = useState<string | null>(() => params.get('rec'));
  /* O período da barra: o dia do recebimento. */
  const [de, setDe] = useState(() => params.get('de') ?? '');
  const [ate, setAte] = useState(() => params.get('ate') ?? '');
  /*
   * A ordenação por COLUNA.
   *
   * «Colocar uma seta para ordenar em ordem crescente ou decrescente ao
   *  lado de cada palavra da 1ª linha» — Testesolo, 02/09/2026.
   *
   * O sentido só vale para as ordenações de coluna: as do seletor («mais
   * recentes», «aqui há mais tempo») já trazem o sentido no significado
   * — «mais recentes ao contrário» não quer dizer nada.
   *
   * `col_recebimento` é data, e por isso abre decrescente: numa lista de
   * chegadas o que se procura é a de ontem, não a de 2019.
   */
  const { ordem, sentido, trocar: trocarOrdem, definir: setOrdem } =
    useOrdenacao('codigo',
    { descendentes: ['col_recebimento'] });

  /*
   * A seleção para a ação em lote.
   *
   * `Set` de id, e não um campo `selecionada` dentro de cada linha: a
   * lista é recarregada a cada tecla da busca, e a marcação tem que
   * sobreviver a isso. Guardar o estado dentro do dado recarregado seria
   * perdê-lo justamente quando o técnico filtra para achar o resto.
   */
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  /*
   * No celular as caixas de marcar só aparecem depois de «Selecionar»
   * (309): sem elas, o cartão inteiro é o alvo que abre a amostra (RN-251
   * c), e a marcação — o gesto de quem vai guardar ou etiquetar um lote —
   * é um modo que se liga. Com algo marcado o modo fica ligado (a janela
   * que encolheu com doze marcadas não as esconde).
   */
  const [selecionando, setSelecionando] = useState(false);
  const emSelecao = selecionando || marcadas.size > 0;
  const [guardando, setGuardando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  /* A folha de etiquetas vem por `fetch` com o cabeçalho de sessão e é
     mostrada num quadro isolado — aba nova não leva o `Authorization`. */
  const [folha, setFolha] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      if (estado) q.set('estado', estado);
      if (cliente) q.set('cliente', cliente);
      if (os) q.set('os', os);
      if (de) q.set('de', de);
      if (ate) q.set('ate', ate);
      if (ordem !== 'codigo') q.set('ordem', ordem);
      if (ordem.startsWith('col_')) q.set('sentido', sentido);
      const novas = await api<Amostra[]>(`/amostras?${q}`);
      setLinhas(novas);
      /*
       * A marcação só sobrevive ao que continua na lista.
       *
       * Guardar em lote uma amostra que o filtro tirou da tela seria
       * mover algo que a pessoa não está mais vendo — e ela descobriria
       * depois, na trilha.
       */
      setMarcadas((m) => {
        if (m.size === 0) return m;
        const visiveis = new Set(novas.map((a) => a.id));
        const restou = new Set([...m].filter((id) => visiveis.has(id)));
        return restou.size === m.size ? m : restou;
      });
    } catch (e) { setErro(e); }
  }, [busca, estado, cliente, os, de, ate, ordem, sentido]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  if (lendo) {
    return (
      <LeitorQr
        aoLer={(codigo) => {
          setLendo(false);
          navegar(`/amostra/${encodeURIComponent(codigo)}`);
        }}
        aoCancelar={() => setLendo(false)}
      />
    );
  }

  const RECORTES: (ItemDoResumo & { f(a: Amostra): boolean })[] = [
    { chave: 'guardar', rotulo: 'Falta guardar', aux: 'soltas pelo laboratório', tom: 'atencao',
      numero: 0, f: (a) => a.estado_custodia === 'a_guardar' || !!a.precisa_guardar },
    { chave: 'pendentes', rotulo: 'Com ensaio pendente', aux: 'ainda têm trabalho', tom: 'andamento',
      numero: 0, f: (a) => Number(a.ensaios_pendentes) > 0 },
    { chave: 'semos', rotulo: 'Sem OS', aux: 'chegaram sem ordem de serviço', tom: 'critico',
      numero: 0, f: (a) => !!a.sem_os },
  ];
  const rec = RECORTES.find((r) => r.chave === recorte) ?? null;
  const resumo = RECORTES.map((r) => ({ ...r, numero: (linhas ?? []).filter(r.f).length }));
  const recortadas = rec ? (linhas ?? []).filter(rec.f) : (linhas ?? []);
  /* A pergunta da lista: mudou, o celular recomeça em 30. */
  const pag = usePaginacao(recortadas, 50,
    [busca, estado, cliente, os, de, ate, recorte].join('|'));

  const ativos: FiltroAtivo[] = [];
  if (rec) ativos.push({ chave: 'rec', texto: rec.rotulo, tirar: () => setRecorte(null) });
  if (estado) ativos.push({ chave: 'est', texto: `Situação: ${ESTAGIO[estado] ?? rotulo(estado)}`, tirar: () => setEstado(null) });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => { setCliente(null); setOs(null); } });
  if (os) ativos.push({ chave: 'os', texto: 'OS escolhida', tirar: () => setOs(null) });
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (de || ate) ativos.push({ chave: 'per', texto: `Recebidas: ${de ? data(de) : '…'} – ${ate ? data(ate) : '…'}`, tirar: () => { setDe(''); setAte(''); } });
  /* O «Limpar filtros» da faixa: tudo. */
  const limparFiltros = () => {
    setRecorte(null); setEstado(null); setCliente(null); setOs(null);
    setBusca(''); setDe(''); setAte('');
  };
  /* O número e o «Limpar» da folha do celular (309, Ruling R13): só o que
     mora na folha — a busca e a pílula estão à vista e ficam. */
  const naFolha = ativos.filter((f) => ['est', 'cli', 'os', 'per'].includes(f.chave)).length;
  const limparDaFolha = () => {
    setEstado(null); setCliente(null); setOs(null); setDe(''); setAte('');
  };
  const textoDaOrdem = ordem.startsWith('col_')
    ? `pela coluna ${sentido === 'desc' ? '↓' : '↑'}`
    : ORDENS.find((o) => o.chave === ordem)?.texto ?? 'mais recentes';

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          {estreito ? (
            /* No celular (309) a câmera é um ícone de 44 px ao lado do
               título — o botão escrito empurrava a lista uma linha abaixo. */
            <div className="titulo-e-acao">
              <h1>Amostras</h1>
              <button type="button" className="icone-botao" aria-label="Ler QR"
                title="Ler QR" onClick={() => setLendo(true)}>
                <Icone nome="qr" />
              </button>
            </div>
          ) : <h1>Amostras</h1>}
          <p className="sub">O acervo — onde está cada uma e o que falta nela. A chegada mais recente primeiro.</p>
        </div>
        {/*
          A fila de descarte é outra tela, com outra pergunta («o que
          pode sair da prateleira?») — por isso link, e não filtro. No
          celular ela sai daqui: está no «Mais» (309).
        */}
        {!estreito && <Link to="/descarte" className="botao secundario">Fila de descarte</Link>}
        {/*
          A câmera no cabeçalho, e não escondida num menu: na prateleira,
          o caminho mais curto entre o tubo na mão e a tela dele é ler a
          etiqueta.
        */}
        {!estreito && (
          <button type="button" className="botao" onClick={() => setLendo(true)}>
            <span aria-hidden="true">▣</span> Ler QR
          </button>
        )}
      </div>

      <ResumoQueFiltra rotulo="Resumo das amostras" itens={resumo} ativo={recorte}
        aoEscolher={setRecorte} />

      <BarraDeLista rotulo="Filtros das amostras" ativos={naFolha}
        aoLimpar={limparDaFolha} total={linhas ? recortadas.length : undefined}>
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Código, identificação, cliente, obra, OS, furo ou camada"
          rotulo="Buscar amostra" />
        <Periodo valor={{ de, ate }} rotulo="Recebidas"
          aoMudar={(v) => { setDe(v.de); setAte(v.ate); }}
          atalhos={['hoje', 'semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <Seletor naBarra rotulo="Situação" valor={estado} opcoes={SITUACOES}
          aoMudar={setEstado} todos="Todas as situações" />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={(c) => { setCliente(c); setOs(null); }} />
        <SeletorDeOs naBarra valor={os} cliente={cliente} aoMudar={setOs} />
        {/*
          A ordem da lista, ao lado dos filtros (padrão Carbon): o acervo
          tem três perguntas de ordem que não são colunas — a chegada, o
          tempo na prateleira e o trabalho pendente. As colunas da tabela
          continuam clicáveis; quando uma delas manda, o menu diz isso.
        */}
        <Seletor naBarra rotulo="Ordenar" valor={ordem.startsWith('col_') ? 'coluna' : ordem}
          opcoes={[
            ...ORDENS.map((o) => ({ valor: o.chave, texto: o.texto })),
            ...(ordem.startsWith('col_') ? [{ valor: 'coluna', texto: 'pela coluna escolhida' }] : []),
          ]}
          aoMudar={(v) => setOrdem(v && v !== 'coluna' ? v : 'codigo')} todos="mais recentes" />
      </BarraDeLista>

      <FiltrosAtivos itens={ativos} aoLimpar={limparFiltros}
        contagem={linhas ? `${recortadas.length} de ${linhas.length} amostras` : undefined}
        acao={estreito && linhas && (recortadas.length > 0 || emSelecao) ? (
          <button type="button" className="botao secundario botao-selecionar"
            onClick={() => {
              if (emSelecao) { setSelecionando(false); setMarcadas(new Set()); }
              else setSelecionando(true);
            }}>
            {emSelecao ? 'Cancelar seleção' : 'Selecionar'}
          </button>
        ) : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">

            {marcadas.size > 0 && (
              <BarraDeLote
                quantas={marcadas.size}
                ocupada={guardando}
                aoLimpar={() => setMarcadas(new Set())}
                aoEtiquetar={async (formato) => {
                  setErro(null);
                  try {
                    setFolha(await api<string>(
                      `/amostras/etiquetas?ids=${[...marcadas].join(',')}${
                        formato ? `&formato=${formato}` : ''}&registrar=lista`,
                      { cru: true }));
                  } catch (e) { setErro(e); }
                }}
                aoGuardar={async (localId, observacao) => {
                  setGuardando(true);
                  setErro(null);
                  try {
                    const r = await api<{
                      local: string; guardadas: number;
                      detalhe: { pulados: Array<{ codigo: string; porque: string }> };
                    }>('/amostras/guardar-em-lote', {
                      metodo: 'POST',
                      corpo: {
                        amostraIds: [...marcadas], localId, observacao,
                      },
                    });
                    setMarcadas(new Set());
                    await carregar();
                    /*
                     * O que NÃO foi é a informação que importa. Um
                     * «pronto» que esconde três pulos deixa o técnico
                     * achando que guardou doze — e ele só descobre no
                     * inventário do mês seguinte.
                     */
                    const p = r.detalhe.pulados;
                    setRecado(
                      `${r.guardadas} amostra(s) em ${r.local}.`
                      + (p.length
                        ? ` ${p.length} não foi(ram): `
                          // 303: o pulo é dito pelo nome do cliente (a rota só
                          // devolve o código; o nome vem da lista já carregada).
                          + p.map((x) => {
                            const l = linhas?.find((y) => y.codigo === x.codigo);
                            return `${l ? nomeDaAmostra(daApi(l)) : x.codigo} (${x.porque})`;
                          }).join('; ')
                        : ''));
                  } catch (e) { setErro(e); }
                  finally { setGuardando(false); }
                }}
              />
            )}

            {folha && (
              <JanelaDocumento
                titulo="Folha de etiquetas"
                corpo={folha}
                aoFechar={() => setFolha(null)}
              />
            )}

            {recado && (
              <div className="aviso" role="status">
                {recado}{' '}
                <button type="button" className="botao discreto"
                  onClick={() => setRecado(null)}>Fechar</button>
              </div>
            )}

            {recortadas.length === 0 ? (
              <Vazio>
                {ativos.length > 0
                  ? 'Nenhuma amostra com estes filtros. Use «Limpar filtros» para ver todas.'
                  : 'Nenhuma amostra ainda. As amostras nascem no recebimento — nunca soltas.'}
              </Vazio>
            ) : (
              /*
               * O acervo numa tabela com ficha (onda 219). A marca, a
               * amostra e a situação ficam; origem, onde está e ensaios são
               * detalhe e, com a ficha aberta, moram nela. A marca é caixa
               * de seleção: clicá-la marca a amostra, não abre a ficha.
               */
              <TabelaComFicha
                linhas={pag.visiveis}
                chave={(a) => a.id}
                nome={(a) => `Amostra ${nomeDaAmostra(daApi(a, true))}`}
                rotulo="Amostras"
                classeDaLinha={(a) => (marcadas.has(a.id) ? 'marcada' : undefined)}
                colunas={colunasDasAmostras({
                  linhas: pag.visiveis, marcadas, setMarcadas, ordem, sentido, trocar: trocarOrdem,
                })}
                ficha={fichaDaAmostra}
                lixeira={{ tipo: 'amostra', id: (a) => a.id, rotulo: (a) => `amostra ${nomeDaAmostra(daApi(a, true))}`,
                  aoApagar: () => { void carregar(); } }}
                cartao={(a) => <CartaoDaAmostra a={a} />}
                destino={(a) => `/amostra/${encodeURIComponent(a.codigo)}`}
                marca={emSelecao ? (a) => (
                  <CaixaDeMarcar a={a} marcadas={marcadas} setMarcadas={setMarcadas} />
                ) : undefined}
              />
            )}

            <RodapeDaLista {...pag} teto={500} ordem={textoDaOrdem} />

            <p className="rodape-regra">
              Amostra não se cria solta: ela nasce de um recebimento, com
              cadeia de custódia desde a entrega. O que esta tela mostra é
              o acervo; o que aconteceu com cada uma está na ficha dela.
            </p>
          </div>
        )}
    </div>
  );
}

/**
 * Furo e profundidade — a linha de baixo do nome. Desde a onda 303 (RN-248)
 * a identificação do cliente é o próprio nome da amostra, em cima; repeti-la
 * aqui seria dizer a mesma coisa duas vezes.
 */
function origemDaAmostra(a: Amostra): string {
  const faixa = faixaProfundidade(a.profundidade_inicial, a.profundidade_final);
  return [a.ponto_investigacao, faixa && `${faixa} m`]
    .filter(Boolean).join(' · ') || '—';
}

/**
 * «Sem OS» dito com todas as letras.
 *
 * A célula ficava em branco, e branco não distingue «ainda não atribuída»
 * de «a consulta não trouxe». A RN-028 permite amostra sem OS DE PROPÓSITO
 * — cliente manda material sem avisar (0011) —, então o estado é legítimo
 * e temporário, e é justamente por ser temporário que precisa aparecer:
 * alguém tem que atribuir.
 */
function OrigemCliente({ a }: { a: Amostra }) {
  return (
    <>
      {clienteEObra(a.cliente, a.obra_nome).map((t) => (
        <span className="sub" key={t}>{t}</span>
      ))}
      {a.sem_os ? (
        <span className="sub aviso-linha">sem OS — falta atribuir</span>
      ) : (
        a.os_numero && <span className="sub">OS {a.os_numero}</span>
      )}
    </>
  );
}

/**
 * Onde está. A frase vem do BANCO (`onde_esta`, na 0062), e não é montada
 * aqui: a mesma pergunta aparece na lista, na ficha, no PDF de recebimento
 * e no balanço. E a idade vem junto da data, e não no lugar dela: «20/08»
 * obriga a fazer a conta; «há 9 dias» sozinho esconde de quando se fala.
 */
function OndeEsta({ a }: { a: Amostra }) {
  return (
    <>
      {a.precisa_guardar ? <span className="nada">{a.onde_esta}</span> : a.onde_esta}
      {a.datahora_recebimento && (
        <span className="sub">
          recebida em {data(a.datahora_recebimento)}
          {a.dias_no_laboratorio !== null
            && a.dias_no_laboratorio !== undefined
            && Number(a.dias_no_laboratorio) > 0
            && ` · há ${a.dias_no_laboratorio} dia(s)`}
        </span>
      )}
    </>
  );
}

/**
 * O prazo de guarda só aparece quando está perto de vencer. Uma coluna que
 * mostra «faltam 340 dias» em toda linha é ruído que ensina a ignorar a
 * coluna — e aí o dia em que faltam três também passa batido.
 */
function prazoDeGuarda(a: Amostra): string | null {
  if (a.dias_para_descarte == null || a.dias_para_descarte > 30) return null;
  return a.dias_para_descarte < 0
    ? `guarda vencida há ${-a.dias_para_descarte} dia(s)`
    : `guarda vence em ${a.dias_para_descarte} dia(s)`;
}

function SelosDaAmostra({ a }: { a: Amostra }) {
  return (
    <>
      <Selo tom={TOM[a.status] ?? 'neutro'}>{rotulo(a.status)}</Selo>
      <Selo tom={TOM_CUSTODIA[a.estado_custodia] ?? 'neutro'}>
        {ESTAGIO[a.estado_custodia] ?? rotulo(a.estado_custodia)}
      </Selo>
    </>
  );
}

/**
 * A caixa de marcar uma amostra: a mesma na coluna da tabela e ao lado do
 * cartão do celular (309), com o mesmo nome para quem ouve a tela.
 */
function CaixaDeMarcar({ a, marcadas, setMarcadas }: {
  a: Amostra;
  marcadas: Set<string>;
  setMarcadas: (f: (m: Set<string>) => Set<string>) => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={`Marcar ${nomeDaAmostra(daApi(a, true))}`}
      checked={marcadas.has(a.id)}
      /*
       * O valor é lido ANTES do atualizador: `e.currentTarget` é
       * anulado quando o manipulador retorna, e lê-lo dentro do
       * callback de `setMarcadas` derrubava a tela inteira.
       */
      onChange={(e) => {
        const ligado = e.currentTarget.checked;
        setMarcadas((m) => {
          const n = new Set(m);
          if (ligado) n.add(a.id); else n.delete(a.id);
          return n;
        });
      }}
    />
  );
}

/**
 * O cartão da amostra no celular (309, spec 06 §6 item 7): o nome que o
 * cliente deu em destaque, o código AM e a OS embaixo (303 — a lista mistura
 * OS), o furo e a profundidade, o cliente, onde ela está e o selo da
 * situação. O que as colunas da tabela dizem, de cima para baixo.
 *
 * Onde está é uma linha de apoio, com a frase do banco (`onde_esta`), ou
 * «falta guardar» apagado quando ela ainda não tem lugar (R46) — e não um
 * segundo selo: o selo do ciclo não dizia EM QUE lugar ela está, e dois
 * selos lado a lado brigavam com a situação.
 */
function CartaoDaAmostra({ a }: { a: Amostra }) {
  return (
    <>
      <NomeDaAmostra amostra={daApi(a, true)} />
      <span className="cartao-meta">{origemDaAmostra(a)}</span>
      <span className="cartao-cli">
        {a.cliente ?? <span className="nada">sem cliente</span>}
        {a.sem_os && <> · <span className="aviso-linha">sem OS — falta atribuir</span></>}
      </span>
      <span className="cartao-meta">
        {a.precisa_guardar ? <span className="nada">falta guardar</span> : a.onde_esta}
      </span>
      <span className="cartao-selos">
        <Selo tom={TOM[a.status] ?? 'neutro'}>{rotulo(a.status)}</Selo>
      </span>
    </>
  );
}

/** As colunas do acervo (onda 219). */
function colunasDasAmostras({
  linhas, marcadas, setMarcadas, ordem, sentido, trocar,
}: {
  linhas: Amostra[];
  marcadas: Set<string>;
  setMarcadas: (f: (m: Set<string>) => Set<string>) => void;
  ordem: string; sentido: 'asc' | 'desc'; trocar: (campo: string) => void;
}): ColunaComFicha<Amostra>[] {
  const col = (campo: string, texto: string, opcional = false) => (
    <ColunaOrdenavel campo={campo} texto={texto} opcional={opcional}
      ordem={ordem} sentido={sentido} aoTrocar={trocar} />
  );
  return [
    {
      chave: 'marca',
      /*
       * Marcar TODAS as visíveis — as visíveis, e não «o acervo». O botão
       * que seleciona o que a pessoa não está vendo é como se move
       * material por engano.
       */
      cabeca: (
        <th scope="col" className="col-marca">
          <input
            type="checkbox"
            aria-label="Marcar todas as amostras da lista"
            checked={linhas.length > 0 && linhas.every((a) => marcadas.has(a.id))}
            onChange={(e) => {
              const ligado = e.currentTarget.checked;
              setMarcadas(() => (ligado ? new Set(linhas.map((a) => a.id)) : new Set()));
            }}
          />
        </th>
      ),
      celula: (a) => (
        <td className="col-marca">
          <CaixaDeMarcar a={a} marcadas={marcadas} setMarcadas={setMarcadas} />
        </td>
      ),
    },
    {
      chave: 'amostra',
      cabeca: col('col_amostra', 'Amostra'),
      celula: (a) => (
        <th scope="row">
          {/* 303 (RN-248): o nome do cliente em destaque; o código e a OS
              embaixo — a lista mistura amostras de todas as OS. */}
          <Link to={`/amostra/${encodeURIComponent(a.codigo)}`}>
            <NomeDaAmostra amostra={daApi(a, true)} />
          </Link>
          <span className="sub">{origemDaAmostra(a)}</span>
          <span className="sub">
            {[rotulo(a.tipo_amostra), rotulo(a.tipo_acondicionamento)]
              .filter(Boolean).join(' · ')}
          </span>
        </th>
      ),
    },
    {
      chave: 'origem', detalhe: true,
      cabeca: col('col_origem', 'Origem', true),
      celula: (a) => <td className="col-opcional"><OrigemCliente a={a} /></td>,
    },
    {
      chave: 'local', detalhe: true,
      cabeca: col('col_local', 'Onde está', true),
      celula: (a) => <td className="col-opcional"><OndeEsta a={a} /></td>,
    },
    {
      /*
       * A outra metade do subtítulo: «onde está cada uma E O QUE FALTA
       * NELA». Até 30/08 nenhuma coluna dizia o que falta (doc 48).
       */
      chave: 'ensaios', detalhe: true,
      cabeca: col('col_ensaios', 'Ensaios', true),
      celula: (a) => <td className="col-opcional"><Ensaios amostra={a} /></td>,
    },
    {
      chave: 'situacao',
      cabeca: col('col_situacao', 'Situação'),
      celula: (a) => {
        const guarda = prazoDeGuarda(a);
        return (
          <td>
            <SelosDaAmostra a={a} />
            {guarda && <span className="sub" style={{ display: 'block' }}>{guarda}</span>}
          </td>
        );
      },
    },
  ];
}

/** A ficha da amostra ao lado do acervo: o que as colunas recolhidas diziam. */
function fichaDaAmostra(a: Amostra): FichaDaLinha {
  const guarda = prazoDeGuarda(a);
  return {
    rotulo: 'Amostra',
    titulo: <NomeDaAmostra amostra={daApi(a, true)} forte={false} />,
    sub: (
      <>
        <span>{origemDaAmostra(a)}</span>
        <span>
          {[rotulo(a.tipo_amostra), rotulo(a.tipo_acondicionamento)]
            .filter(Boolean).join(' · ')}
        </span>
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos"><SelosDaAmostra a={a} /></div>
        {a.sem_os && (
          <div className="tf-aviso critico">
            <strong>Sem OS.</strong> Alguém tem que atribuir esta amostra a uma
            ordem de serviço.
          </div>
        )}
        {!a.sem_os && a.precisa_guardar && (
          <div className="tf-aviso">
            <strong>Falta guardar.</strong> A amostra ainda não tem lugar no
            acervo.
          </div>
        )}
        {guarda && a.dias_para_descarte != null && a.dias_para_descarte < 0 && (
          <div className="tf-aviso">
            <strong>{guarda[0].toUpperCase() + guarda.slice(1)}.</strong>
          </div>
        )}
        <CamposDaFicha itens={[
          ['Cliente e obra', clienteEObra(a.cliente, a.obra_nome).join(' · ')],
          ['OS', a.os_numero && <span className="mono">{a.os_numero}</span>],
          ['Camada', a.camada],
          ['Onde está', <OndeEsta a={a} />],
          ['Ensaios', <Ensaios amostra={a} />],
          ['Guarda', guarda],
        ]} />
      </>
    ),
    acoes: (
      <>
        {a.os_numero && (
          <Link className="botao secundario"
            to={`/os/${encodeURIComponent(a.os_numero)}`}>Ver OS</Link>
        )}
        <Link className="botao" to={`/amostra/${encodeURIComponent(a.codigo)}`}>
          Abrir amostra
        </Link>
      </>
    ),
  };
}

/**
 * O que falta nesta amostra — a metade da promessa que faltava.
 *
 * «Coluna ENSAIOS: "2 de 5 concluídos", e embaixo "pendente:
 *  adensamento, CD". É o que transforma inventário em ferramenta de
 *  programação.» — doc 48.
 *
 * Os NOMES, e não a contagem sozinha, são o ponto. «3 pendentes» manda o
 * gestor abrir a ficha; «pendente: adensamento, CD» faz ele decidir a
 * bancada de terça na própria lista.
 *
 * A frase vem do banco (`fn_amostra_pendentes_texto`, 0067) pelo mesmo
 * motivo de `onde_esta`: ela aparece aqui, na ficha e no balanço, e três
 * lugares montando a mesma frase é como três lugares passam a discordar.
 */
function Ensaios({ amostra }: { amostra: Amostra }) {
  const total = Number(amostra.ensaios ?? 0);
  if (total === 0) {
    /*
     * Amostra sem ensaio programado não é «0 de 0»: ela está esperando
     * alguém decidir o que fazer com ela, que é uma pendência de
     * programação e não de bancada.
     */
    return <span className="nada">nenhum ensaio programado</span>;
  }
  const aprovados = Number(amostra.ensaios_aprovados ?? 0);
  const pct = amostra.avanco_pct === null || amostra.avanco_pct === undefined
    ? null : Number(amostra.avanco_pct);

  return (
    <div className="avanco">
      {pct !== null && (
        <BarraDeAvanco pct={pct}
          rotulo={`Avanço dos ensaios da amostra ${nomeDaAmostra(daApi(amostra))}`} />
      )}
      <span className="mono">{aprovados} de {total} concluído(s)</span>
      {amostra.ensaios_pendentes_texto && (
        <span className="sub">
          pendente: {amostra.ensaios_pendentes_texto}
        </span>
      )}
    </div>
  );
}

/**
 * A ação em lote.
 *
 * «Um acervo sem ação em lote obriga o técnico a dez idas e voltas, e é
 *  esse atrito que faz as pessoas pararem de registrar movimentação. Que
 *  é como a coluna "ONDE ESTÁ" fica vazia.» — doc 48.
 *
 * O gesto real é um só: o técnico tira doze amostras da bancada e põe as
 * doze na estante B. Fazer disso doze telas transforma um registro de
 * trinta segundos numa tarefa de dez minutos — e tarefa de dez minutos
 * não é feita.
 *
 * Fica GRUDADA no topo do quadro, e não no rodapé: quem marcou doze
 * amostras rolou a lista para marcá-las, e uma barra no fim da página
 * seria uma barra que ele não vê.
 */
function BarraDeLote({
  quantas, ocupada, aoLimpar, aoGuardar, aoEtiquetar,
}: {
  quantas: number;
  ocupada: boolean;
  aoLimpar(): void;
  aoGuardar(localId: string, observacao?: string): void | Promise<void>;
  aoEtiquetar(formato?: 'rolo'): void | Promise<void>;
}) {
  const [locais, setLocais] = useState<LocalOpcao[] | null>(null);
  const [local, setLocal] = useState('');
  const [obs, setObs] = useState('');

  useEffect(() => {
    api<LocalOpcao[]>('/locais?situacao=ativo')
      .then(setLocais)
      .catch(() => setLocais([]));
  }, []);

  return (
    <div className="barra-lote" role="region"
      aria-label="Ação em lote sobre as amostras marcadas">
      <strong>{quantas} marcada(s)</strong>

      <label>
        <span className="rotulo-mini">guardar em</span>
        <select value={local} onChange={(e) => setLocal(e.target.value)}
          aria-label="Local de destino">
          <option value="">escolha o lugar…</option>
          {(locais ?? []).map((l) => (
            <option key={l.id} value={l.id}>{l.nome} · {l.tipo_nome}</option>
          ))}
        </select>
      </label>

      {/*
        A observação é opcional aqui e obrigatória em nenhum lugar — mas
        ela vira o motivo do elo da cadeia, e «organização do acervo de
        segunda» explica seis meses depois o que um horário sozinho não
        explica.
      */}
      <input
        type="text"
        value={obs}
        maxLength={200}
        placeholder="por quê (opcional): organização do acervo"
        aria-label="Motivo da movimentação"
        onChange={(e) => setObs(e.target.value)}
      />

      <button
        type="button"
        className="botao"
        disabled={!local || ocupada}
        onClick={() => aoGuardar(local, obs.trim() || undefined)}
      >
        {ocupada ? 'Guardando…' : 'Guardar as marcadas'}
      </button>
      {/*
        A outra metade do pedido do doc 48: «imprimir etiquetas QR de uma
        vez». É o mesmo gesto do outro botão — doze amostras numa caixa,
        doze etiquetas numa folha —, e separá-los em duas telas traria de
        volta o atrito que a barra existe para tirar.

        A folha já sabe reaproveitar folha usada (`deslocamento`), então
        vale para as duas etiquetas que sobraram da impressão de ontem.
      */}
      <button type="button" className="botao discreto"
        onClick={() => aoEtiquetar()}>
        Etiquetas em folha A4
      </button>
      {/*
        O rolo térmico: 100 × 50 mm na Zebra, uma por página (onda 253 —
        era 50 × 30). A mesma seleção, o mesmo QR e o mesmo desenho da
        folha A4 (`RoloService`) — muda o portador, e por isso é botão ao
        lado, e não outra tela.
      */}
      <button type="button" className="botao discreto"
        onClick={() => aoEtiquetar('rolo')}>
        Etiquetas 100 × 50 mm
      </button>
      <button type="button" className="botao discreto" onClick={aoLimpar}>
        Desmarcar
      </button>
    </div>
  );
}

